import { isUndefined } from 'lodash-es';
import { type ReactNode, Children } from 'react';

import { isSlot } from '../../../core/utils/is-slot.js';
import type { AnnotationsTooltipProps } from '../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsMarkerConfig } from '../../core/components/annotations/types/annotations-marker-props.js';
import type { AnnotationsTrackConfig } from '../../core/components/annotations/types/annotations-track-props.js';
import { populateMissingMarkerProps } from '../../core/components/annotations/utils/populate-missing-marker-props.js';
import { populateMissingTrackProps } from '../../core/components/annotations/utils/populate-missing-track-props.js';
import type { XAxisProps } from '../components/axis/types/annotations-x-axis-props.js';
import { Marker } from '../slots/Marker.js';
import { Tooltip } from '../slots/Tooltip.js';
import { Track } from '../slots/Track.js';
import { XAxis } from '../slots/XAxis.js';

const reactNodeToArray = (children: ReactNode) => Children.toArray(children);

const isValidMarker = (marker: AnnotationsMarkerConfig) => {
  const valid = Number.isFinite(+marker.start);

  if (!isUndefined(marker.end)) {
    return valid && Number.isFinite(+marker.end);
  }

  return valid;
};

export const iterateSlots = (children: ReactNode) => {
  const slots = reactNodeToArray(children);

  const tracks: AnnotationsTrackConfig[] = [];
  const aggregatedMarkers: AnnotationsMarkerConfig[] = [];

  let tooltipProps: AnnotationsTooltipProps = {};
  let xAxisProps: XAxisProps = {};

  slots.forEach((child) => {
    if (isSlot(child, XAxis)) {
      xAxisProps = child.props;
    } else if (isSlot(child, Track)) {
      const markersChildren = reactNodeToArray(child.props.children);
      const { children, ...trackProps } = child.props;
      const currentTrack = {
        ...trackProps,
        markers: [] as AnnotationsMarkerConfig[],
      };

      markersChildren.forEach((markerChild) => {
        if (isSlot(markerChild, Marker)) {
          const marker = populateMissingMarkerProps(
            markerChild.props,
            trackProps,
          );

          if (isValidMarker(marker)) {
            currentTrack.markers.push(marker);
            aggregatedMarkers.push(marker);
          }
        }
      });

      const track = populateMissingTrackProps(currentTrack);
      tracks.push(track);
    } else if (isSlot(child, Tooltip)) {
      tooltipProps = child.props;
    }
  });

  return { tracks, aggregatedMarkers, xAxisProps, tooltipProps };
};
